import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

// Material UI components
import { 
  TextField, 
  Button, 
  IconButton, 
  Typography, 
  InputAdornment, 
  Link as MuiLink,
  Tooltip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HelpIcon from '@mui/icons-material/Help';

import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { auth } from '../firebaseConfig';

// Local CSS module for signup
import styles from './SignupPage.module.css';

/**
 * SignUp – renders the registration form for new GigemAI users.
 * 
 * Features:
 * - Email & password fields with real‑time error clearing.
 * - Password confirmation matching.
 * - Firebase Auth user creation (`handleSignUp`):
 *     • Validates matching passwords.
 *     • Calls `createUserWithEmailAndPassword`.
 *     • Sends email verification via `sendEmailVerification`.
 *     • Alerts user and redirects to `/login` on success.
 *     • Handles common Firebase errors with user‑friendly messages.
 * - Toggle password visibility for both fields (`toggleShowPassword`, `toggleShowConfirmPassword`).
 * 
 * State:
 * - `email`, `password`, `confirmPassword`, `error`
 * - `showPassword`, `showConfirmPassword`
 * 
 * Hooks & Context:
 * - `useNavigate` for routing to login page.
 * - Firebase `auth` instance for account creation and verification.
 * 
 * Author: Galo Alonso Peralta Espinoza
 * Date: 04-17-2025
 */

function SignUp() {
  // State variables
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  // Toggle password visibility
  const toggleShowPassword = () => {
    setShowPassword(prev => !prev);
  };
  const toggleShowConfirmPassword = () => {
    setShowConfirmPassword(prev => !prev);
  };

  // Handle the SignUp button click
  const handleSignUp = async () => {

    // TEMPORARY: Restrict to @gigemai.com emails
    // 0. Restrict to @gigemai.com emails
    // COMMENTED OUT FOR TESTING - Allow any email domain
    // if (!email.endsWith('@gigemai.com')) {
    //   setError('Only @gigemai.com email addresses are allowed to sign up at this time.');
    //   return;
    // }
    
    // 1. Check if passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match. Please try again.');
      return;
    }
    // 2. Create user with Firebase
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // 3. Send email verification
      await sendEmailVerification(userCredential.user);

      alert('Account created! A verification email has been sent. Please check your inbox.');
      // 4. Redirect user to login page
      navigate('/login');
    } catch (error) {
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('Email already in use. Please use a different email.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email format. Please try again.');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please try again.');
          break;
        default:
          setError(error.message);
      }
    }
  };

  return (
    <main className={styles.signupPage}>
      
      {/* Navbar */}
      <header className={styles.signupNavbar}>
        <div className={styles.signupNavbarContent}>
          <img
            className={styles.signupLogo}
            alt="GigemAI Logo"
            src="gigemai_logo_v2.png"
          />
        </div>
      </header>

      {/* Main content area */}
      <section className={styles.signupContent}>
        
        {/* Header */}
        <div className={styles.signupHeader}>
          <h3 className={styles.signupTitle}>ExtractAI</h3>
          <p className={styles.signupSubtitle}>Signup</p>
        </div>

        {/* Form Container */}  
        <div className={styles.signupFormGlass}>
          <div className={styles.signupFormFieldsCentered}>  
                      
            {/* Email Field with Custom Label */}
            <div className={styles.signupFormGroupCentered}>
              <Typography
                sx={{
                  color: '#1E1E1E',
                  fontSize: '16px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  lineHeight: '22.4px',
                  marginBottom: '8px',
                }}
              >
                Email
              </Typography>
              <TextField
                placeholder="user@example.com"
                variant="outlined"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                size="small"
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  width: '100%',
                  margin: '0 auto',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '& fieldset': { borderColor: '#D9D9D9' },
                    '&:hover fieldset': { borderColor: '#D9D9D9' },
                    '&.Mui-focused fieldset': { borderColor: '#D9D9D9' },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '16px',
                    fontFamily: 'Inter, sans-serif',
                    color: '#000000',
                  },
                  '& .MuiOutlinedInput-input::placeholder': {
                    color: '#B3B3B3',
                  },
                }}
              />
            </div>

            {/* Password and Confirm Password Fields Side by Side */}
            <div className={styles.passwordRow}>
              <div className={styles.signupFormGroupCentered} style={{ width: '50%' }}>
                <Typography
                  sx={{
                    color: '#1E1E1E',
                    fontSize: '16px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    lineHeight: '22.4px',
                    marginBottom: '8px',
                  }}
                >
                  Password
                </Typography>
                <TextField
                  placeholder="password123"
                  variant="outlined"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  size="small"
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    width: '100%',
                    margin: '0 auto',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#D9D9D9' },
                      '&:hover fieldset': { borderColor: '#D9D9D9' },
                      '&.Mui-focused fieldset': { borderColor: '#D9D9D9' },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: '16px',
                      fontFamily: 'Inter, sans-serif',
                      color: '#000000',
                    },
                    '& .MuiOutlinedInput-input::placeholder': {
                      color: '#B3B3B3',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={toggleShowPassword} edge="end">
                          {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </div>
              <div className={styles.signupFormGroupCentered} style={{ width: '50%' }}>
                <Typography
                  sx={{
                    color: '#1E1E1E',
                    fontSize: '16px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    lineHeight: '22.4px',
                    marginBottom: '8px',
                  }}
                >
                  Re-enter Password
                </Typography>
                <TextField
                  placeholder="password123"
                  variant="outlined"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  size="small"
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    width: '100%',
                    margin: '0 auto',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#D9D9D9' },
                      '&:hover fieldset': { borderColor: '#D9D9D9' },
                      '&.Mui-focused fieldset': { borderColor: '#D9D9D9' },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: '16px',
                      fontFamily: 'Inter, sans-serif',
                      color: '#000000',
                    },
                    '& .MuiOutlinedInput-input::placeholder': {
                      color: '#B3B3B3',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={toggleShowConfirmPassword} edge="end">
                          {showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </div>
            </div>

            {/* Signup Button */}
            <div className={styles.buttonRow}>
              <Button
                variant="contained"
                onClick={handleSignUp}
                className={styles.signUpButton}
                style={{ width: '100%' }}
              >
                Signup
              </Button>
            </div>

            {/* Link to Login */}
            <Typography variant="body2" className={styles.linkWrapper}>
              Already have an account?{' '}
              <MuiLink
                component={RouterLink}
                to="/login"
                underline="always"
                className={styles.simpleLink}
              >
                Log In Here
              </MuiLink>
            </Typography>

            {/* Error Message */}
            {error && (
              <Typography color="error" sx={{ marginTop: '10px' }}>
                {error}
              </Typography>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <span className={styles.footerText}>
          GigemAI
        </span>
      </footer>
    </main>
  );
}

export default SignUp;